// The one place the team edits member facing words.
// Tracking lines, while you wait cards, and every member email live here.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Loader2, RefreshCw, Save, History, Send } from "lucide-react";
import {
  BLOCK_KINDS,
  KIND_LABEL,
  isHtmlBlock,
  HOUSE_FROM_NAME,
  HOUSE_FROM_EMAIL,
  type ContentBlock,
} from "@/lib/contentBlocks";
import { fillPreviewTokens, renderEmailPreview } from "@/lib/emailPreview";
import { useSendCooldown } from "@/components/admin/UserAccessActions";
import { useCurrentAdmin } from "@/hooks/useCurrentAdmin";

interface BlockRevision {
  id: string;
  slug: string;
  version: number;
  subject: string | null;
  preheader: string | null;
  from_name: string | null;
  reply_to: string | null;
  body_markdown: string | null;
  created_at: string;
}

interface Draft {
  subject: string;
  body: string;
  preheader: string;
  fromName: string;
  replyTo: string;
}

/** Email kinds carry a subject line and the sender identity fields. */
function isEmailKind(kind: string): boolean {
  return kind === "email" || kind === "system_email";
}

const PLACEHOLDER_HELP =
  "You can use {{first_name}}, {{una_name}}, {{portal_url}}, {{note}}, {{tracking_number}}, " +
  "{{fedex_tracking}}, {{ein_scheduled_for}}, {{ein_acquired_on}}, {{ca_submitted_on}}, " +
  "{{ca_approved_on}}, {{docs_sent_on}}, {{fedex_delivered_on}}, and {{packet_received_on}}. " +
  "When a date is not known yet, the page shows the matching fallback wording instead, so nothing is invented.";

function hasFancyDash(text: string): boolean {
  return /[\u2013\u2014]/.test(text || "");
}

const DASH_MESSAGE = "Long dashes are not used in member wording. Use a comma, a colon, or two sentences.";

export default function ContentBlocks() {
  const { isSuperAdmin, loading: adminLoading } = useCurrentAdmin();
  const [params, setParams] = useSearchParams();
  const activeKind = params.get("kind") || "tracking";
  const [rows, setRows] = useState<ContentBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<Record<string, string>>({});
  const [history, setHistory] = useState<Record<string, BlockRevision[]>>({});
  const [historyOpen, setHistoryOpen] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const { remaining, cooling, start: startCooldown } = useSendCooldown();


  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from("content_blocks")
      .select("id, slug, kind, title, subject, preheader, from_name, reply_to, body_markdown, member_facing, version, updated_at")
      .order("kind", { ascending: true })
      .order("slug", { ascending: true });
    if (error) toast.error(error.message);
    setRows((data || []) as ContentBlock[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => rows.filter((r) => r.kind === activeKind), [rows, activeKind]);

  const baseDraft = (row?: ContentBlock): Draft => ({
    subject: row?.subject ?? "",
    body: row?.body_markdown ?? "",
    preheader: row?.preheader ?? "",
    fromName: row?.from_name ?? "",
    replyTo: row?.reply_to ?? "",
  });

  const draftFor = (row: ContentBlock) => drafts[row.slug] ?? baseDraft(row);

  const setDraft = (slug: string, patch: Partial<Draft>) => {
    setDrafts((prev) => {
      const row = rows.find((r) => r.slug === slug);
      const base = prev[slug] ?? baseDraft(row);
      return { ...prev, [slug]: { ...base, ...patch } };
    });
  };

  const loadHistory = useCallback(async (slug: string) => {
    setHistoryLoading(slug);
    const { data, error } = await (supabase as any)
      .from("content_block_revisions")
      .select("id, slug, version, subject, preheader, from_name, reply_to, body_markdown, created_at")
      .eq("slug", slug)
      .order("version", { ascending: false })
      .limit(50);
    setHistoryLoading(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    setHistory((prev) => ({ ...prev, [slug]: (data || []) as BlockRevision[] }));
  }, []);

  const toggleHistory = (slug: string) => {
    const next = historyOpen === slug ? null : slug;
    setHistoryOpen(next);
    if (next && !history[next]) void loadHistory(next);
  };

  // Restore writes back through the same save path, so the database checks still run.
  const save = async (row: ContentBlock, override?: Draft) => {
    const draft = override ?? draftFor(row);
    setSaveError((prev) => ({ ...prev, [row.slug]: "" }));
    if (hasFancyDash(draft.body) || hasFancyDash(draft.subject) || hasFancyDash(draft.preheader)) {
      setSaveError((prev) => ({ ...prev, [row.slug]: DASH_MESSAGE }));
      toast.error(DASH_MESSAGE);
      return;
    }
    setSaving(row.slug);
    const { error } = await (supabase as any)
      .from("content_blocks")
      .update({
        subject: isEmailKind(row.kind) ? draft.subject : null,
        preheader: isEmailKind(row.kind) ? draft.preheader || null : null,
        from_name: isEmailKind(row.kind) ? draft.fromName || null : null,
        reply_to: isEmailKind(row.kind) ? draft.replyTo || null : null,
        body_markdown: draft.body,
        updated_by: (await supabase.auth.getUser()).data.user?.id ?? null,
      })
      .eq("id", row.id);
    setSaving(null);
    if (error) {
      setSaveError((prev) => ({ ...prev, [row.slug]: error.message }));
      toast.error(error.message);
      return;
    }
    if (override) setDraft(row.slug, override);
    toast.success("Saved. The earlier wording is kept in history.");
    void load();
    if (history[row.slug]) void loadHistory(row.slug);
  };

  const restore = (row: ContentBlock, rev: BlockRevision) =>
    save(row, {
      subject: rev.subject ?? "",
      body: rev.body_markdown ?? "",
      preheader: rev.preheader ?? "",
      fromName: rev.from_name ?? "",
      replyTo: rev.reply_to ?? "",
    });

  // Sends the wording sitting in the editor right now, saved or not.
  const sendTest = async (row: ContentBlock) => {
    const draft = draftFor(row);
    if (hasFancyDash(draft.body) || hasFancyDash(draft.subject) || hasFancyDash(draft.preheader)) {
      toast.error(DASH_MESSAGE);
      return;
    }
    setTesting(row.slug);
    const { data, error } = await supabase.functions.invoke("message-template-test", {
      body: {
        key: row.slug,
        subject: draft.subject || row.title,
        body: draft.body,
        preheader: draft.preheader,
        from_name: draft.fromName,
        reply_to: draft.replyTo,
      },
    });
    setTesting(null);
    if (error) {
      toast.error("We could not send that test. Please try again.");
      return;
    }
    const result = data as { to?: string; sent?: boolean; parked?: boolean; error?: string };
    startCooldown();
    if (result?.sent) toast.success(`Test sent to ${result.to}.`);
    else if (result?.parked) toast.message("Test saved to the outbox. Mail sending is not switched on yet.");
    else toast.error(result?.error || "We could not send that test.");
  };


  if (adminLoading) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-3">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!isSuperAdmin()) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Content</CardTitle>
            <CardDescription>This area is for the platform owner.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Content</h1>
          <p className="text-sm text-muted-foreground">
            Every word a member reads on the tracking page and in our emails.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {BLOCK_KINDS.map((kind) => (
          <Button
            key={kind}
            size="sm"
            variant={activeKind === kind ? "default" : "outline"}
            onClick={() => setParams({ kind })}
          >
            {KIND_LABEL[kind]}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing in this group yet.</p>
      ) : (
        <div className="space-y-3">
          {visible.map((row) => {
            const draft = draftFor(row);
            const open = openSlug === row.slug;
            return (
              <Card key={row.slug}>
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => setOpenSlug(open ? null : row.slug)}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-base">{row.title}</CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">version {row.version}</Badge>
                      <span className="font-mono text-xs text-muted-foreground">{row.slug}</span>
                    </div>
                  </div>
                  {isHtmlBlock(row.kind) && row.subject && (
                    <CardDescription>{row.subject}</CardDescription>
                  )}
                </CardHeader>

                {open && (
                  <CardContent className="space-y-4">
                    {isEmailKind(row.kind) && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor={`subject-${row.slug}`}>Subject line</Label>
                          <Input
                            id={`subject-${row.slug}`}
                            value={draft.subject}
                            onChange={(e) => setDraft(row.slug, { subject: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`preheader-${row.slug}`}>Preview text</Label>
                          <Input
                            id={`preheader-${row.slug}`}
                            value={draft.preheader}
                            onChange={(e) => setDraft(row.slug, { preheader: e.target.value })}
                          />
                          <p className="text-xs text-muted-foreground">
                            The short line an inbox shows after the subject. Keep it under about 90 characters.
                          </p>
                        </div>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor={`from-${row.slug}`}>Sender name</Label>
                            <Input
                              id={`from-${row.slug}`}
                              value={draft.fromName}
                              placeholder={HOUSE_FROM_NAME}
                              onChange={(e) => setDraft(row.slug, { fromName: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor={`reply-${row.slug}`}>Reply to email</Label>
                            <Input
                              id={`reply-${row.slug}`}
                              value={draft.replyTo}
                              placeholder={HOUSE_FROM_EMAIL}
                              onChange={(e) => setDraft(row.slug, { replyTo: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid gap-4 lg:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor={`body-${row.slug}`}>Wording</Label>
                        <Textarea
                          id={`body-${row.slug}`}
                          rows={12}
                          value={draft.body}
                          onChange={(e) => setDraft(row.slug, { body: e.target.value })}
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-muted-foreground">{PLACEHOLDER_HELP}</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Preview</Label>
                        <div className="rounded-md border bg-background p-4 text-sm leading-relaxed [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_p]:mb-2">
                          {isEmailKind(row.kind) && (
                            <div className="mb-3 rounded-md border bg-muted/40 p-3">
                              <p className="text-sm font-semibold">
                                {fillPreviewTokens(draft.fromName) || HOUSE_FROM_NAME}
                              </p>
                              <p className="text-sm">
                                {fillPreviewTokens(draft.subject) || row.title}
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {fillPreviewTokens(draft.preheader)}
                              </p>
                            </div>
                          )}
                          {isHtmlBlock(row.kind) ? (
                            <iframe
                              title={`Preview of ${row.slug}`}
                              sandbox=""
                              srcDoc={fillPreviewTokens(draft.body) || "<p>Nothing written yet.</p>"}
                              className="h-96 w-full rounded-md border bg-white"
                            />
                          ) : row.kind === "email" ? (
                            <div
                              className="rounded-md bg-white p-4"
                              dangerouslySetInnerHTML={{
                                __html: renderEmailPreview(
                                  row.slug,
                                  draft.body,
                                  row.title,
                                  draft.preheader,
                                ),
                              }}
                            />
                          ) : (
                            <ReactMarkdown>{draft.body || "Nothing written yet."}</ReactMarkdown>
                          )}
                        </div>
                      </div>

                    </div>

                    {saveError[row.slug] && (
                      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
                        <p className="text-sm font-medium">This wording was not saved.</p>
                        <p className="mt-1 whitespace-pre-wrap font-mono text-xs">
                          {saveError[row.slug]}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <Button variant="outline" size="sm" onClick={() => toggleHistory(row.slug)}>
                        <History className="mr-2 h-4 w-4" />
                        {historyOpen === row.slug ? "Hide earlier wording" : "Earlier wording"}
                      </Button>
                      <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => void sendTest(row)}
                        disabled={testing === row.slug || cooling}
                      >
                        {testing === row.slug ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="mr-2 h-4 w-4" />
                        )}
                        {cooling ? `Send test in ${remaining}s` : "Send test"}
                      </Button>

                      <Button onClick={() => void save(row)} disabled={saving === row.slug}>
                        {saving === row.slug ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="mr-2 h-4 w-4" />
                        )}
                        Save this wording
                      </Button>
                      </div>
                    </div>


                    {historyOpen === row.slug && (
                      <div className="space-y-3 rounded-md border p-3">
                        {historyLoading === row.slug ? (
                          <Skeleton className="h-16 w-full" />
                        ) : (history[row.slug] || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No earlier wording is saved yet.
                          </p>
                        ) : (
                          (history[row.slug] || []).map((rev) => (
                            <div key={rev.id} className="space-y-2 border-b pb-3 last:border-b-0 last:pb-0">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline">version {rev.version}</Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(rev.created_at).toLocaleString()}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={saving === row.slug}
                                  onClick={() => void restore(row, rev)}
                                >
                                  Restore this wording
                                </Button>
                              </div>
                              {rev.subject && (
                                <p className="text-xs text-muted-foreground">{rev.subject}</p>
                              )}
                              <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                                {rev.body_markdown || ""}
                              </pre>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
